import { z } from 'zod';

import { getActiveCoachEditToken } from './coach-edit-token';

/**
 * Canonical error envelope (matches the Fastify API's response shape — see
 * apps/api/src/errors/envelope.ts).
 */
export const ApiErrorEnvelope = z.object({
  error: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
  requestId: z.string().optional(),
});
export type ApiErrorEnvelope = z.infer<typeof ApiErrorEnvelope>;

export class ApiError extends Error {
  public readonly status: number;
  public readonly envelope: ApiErrorEnvelope;
  constructor(status: number, envelope: ApiErrorEnvelope) {
    super(`${envelope.error}: ${envelope.message}`);
    this.name = 'ApiError';
    this.status = status;
    this.envelope = envelope;
  }
}

/**
 * Thin wrapper around `fetch`. The API base URL is read from
 * `VITE_API_BASE_URL` (production) or defaults to `/api` (dev — Vite proxies
 * to the local Fastify server).
 */
const apiBaseUrl =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Bearer token for Authorization header. Not used by login itself. */
  accessToken?: string | null;
  /** Abort signal for cancellation (TanStack Query passes one). */
  signal?: AbortSignal;
}

/* ──────────────────  silent session refresh on 401  ──────────────────
 *
 * Access cookie has a 15-min TTL. Without recovery, the dashboard goes
 * stale until the client logs out + back in. We carry a long-lived
 * (30-day) refresh cookie alongside; on a 401 we POST /auth/refresh
 * once and retry the original request. Concurrent 401s share the same
 * in-flight refresh promise (avoids storms when several queries refire
 * after window-focus). If refresh fails, we fire a registered
 * session-lost handler (set by `auth-context`) so the UI can clear
 * local state + redirect to /login.
 *
 * We use raw fetch for the refresh call so it never recurses into
 * `apiRequest` and never triggers its own auto-refresh.
 */

let pendingRefresh: Promise<boolean> | null = null;

async function tryRefreshSession(): Promise<boolean> {
  const url = `${apiBaseUrl}/api/v1/auth/refresh`;
  try {
    const r = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/html',
      },
      credentials: 'include',
      body: '{}',
    });
    return r.ok;
  } catch {
    return false;
  }
}

function ensureFreshSession(): Promise<boolean> {
  if (!pendingRefresh) {
    pendingRefresh = tryRefreshSession().finally(() => {
      pendingRefresh = null;
    });
  }
  return pendingRefresh;
}

type SessionLostHandler = () => void;
let sessionLostHandler: SessionLostHandler | null = null;

/**
 * Registered by `auth-context` once on mount. Called when a 401 is
 * raised on a non-auth request AND the silent refresh attempt also
 * fails — i.e., the user's refresh cookie is gone or invalid.
 * The handler should clear local auth state and redirect to /login.
 */
export function registerSessionLostHandler(fn: SessionLostHandler): () => void {
  sessionLostHandler = fn;
  return () => {
    if (sessionLostHandler === fn) sessionLostHandler = null;
  };
}

function isAuthPath(path: string): boolean {
  return path.startsWith('/api/v1/auth/');
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  return apiRequestInternal<T>(path, options, false);
}

async function apiRequestInternal<T>(
  path: string,
  options: ApiRequestOptions,
  isRetry: boolean,
): Promise<T> {
  const { method = 'GET', body, headers = {}, accessToken, signal } = options;
  const url = `${apiBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`;

  const finalHeaders: Record<string, string> = {
    // Including `text/html` flips the server's `wantsCookies` heuristic
    // (see auth.ts) so it sets HttpOnly + Secure + __Host- cookies for
    // session/refresh tokens instead of returning them in the body. The
    // browser then carries auth via cookies on every credentials:include
    // fetch, so no access token ever lives in JS land. Closes
    // CodeFix-V1 #33 (XSS exfil of localStorage token).
    Accept: 'application/json, text/html',
    ...headers,
  };
  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (accessToken) {
    finalHeaders.Authorization = `Bearer ${accessToken}`;
  }

  let response: Response;
  try {
    const init: RequestInit = { method, headers: finalHeaders, credentials: 'include' };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }
    if (signal) {
      init.signal = signal;
    }
    response = await fetch(url, init);
  } catch (cause) {
    throw new ApiError(0, {
      error: 'NETWORK_ERROR',
      message: cause instanceof Error ? cause.message : 'Network request failed',
    });
  }

  // Silent session refresh on the first 401 — never for /auth/*
  // endpoints (which would recurse) and never on a retry.
  if (response.status === 401 && !isRetry && !isAuthPath(path)) {
    const refreshed = await ensureFreshSession();
    if (refreshed) {
      return apiRequestInternal<T>(path, options, true);
    }
    // Refresh attempt failed — the user has no usable session.
    if (sessionLostHandler) {
      try {
        sessionLostHandler();
      } catch {
        /* swallow handler errors so we still throw the 401 below */
      }
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    if (!response.ok) {
      throw new ApiError(response.status, {
        error: 'NON_JSON_RESPONSE',
        message: `Server returned ${response.status} without JSON body`,
      });
    }
    return undefined as T;
  }

  if (!response.ok) {
    const parsed = ApiErrorEnvelope.safeParse(payload);
    if (parsed.success) {
      throw new ApiError(response.status, parsed.data);
    }
    throw new ApiError(response.status, {
      error: 'UNKNOWN_ERROR',
      message: `Server returned ${response.status} with non-canonical body`,
    });
  }

  return payload as T;
}

/* ────────────────────────  typed endpoints  ──────────────────────── */

/**
 * Server response shape for a successful login. Tokens (when present)
 * are kept off the persistent store — they live only in React state for
 * the lifetime of the page, and the server's HttpOnly cookies do the
 * real auth work. Legacy top-level fields are accepted for test +
 * back-compat purposes but are no longer plumbed into auth-context.
 */
export const LoginSession = z.object({
  user: z
    .object({
      id: z.string(),
      email: z.string().optional(),
      role: z.enum(['head_coach', 'coach', 'client']),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      language: z.enum(['de', 'en']).optional(),
    })
    .optional(),
  /** New server shape — present when the server fell back to body tokens. */
  tokens: z
    .object({
      accessToken: z.string(),
      refreshToken: z.string(),
      refreshExpiresAt: z.string(),
    })
    .optional(),
  /** Legacy server shape — kept optional so older test fixtures keep parsing. */
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
  refreshExpiresAt: z.string().optional(),
  sessionId: z.string().optional(),
});
export type LoginSession = z.infer<typeof LoginSession>;

/**
 * USR-006 — when the user has 2FA confirmed, /auth/login returns this shape
 * instead of a full session. Caller must POST /auth/2fa/verify with the
 * token + a TOTP / recovery code within 5 minutes.
 */
export const LoginMfaRequired = z.object({
  mfa_required: z.literal(true),
  mfa_token: z.string(),
});
export type LoginMfaRequired = z.infer<typeof LoginMfaRequired>;

// Order matters: LoginMfaRequired has required `mfa_required` + `mfa_token`
// fields and must be checked first, otherwise the now-permissive
// LoginSession (every field optional) would happily match an MFA
// response and swallow the MFA branch.
export const LoginResponse = z.union([LoginMfaRequired, LoginSession]);
export type LoginResponse = z.infer<typeof LoginResponse>;

export function isMfaRequired(r: LoginResponse): r is LoginMfaRequired {
  return 'mfa_required' in r && r.mfa_required === true;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const raw = await apiRequest<unknown>('/api/v1/auth/login', {
    method: 'POST',
    body: { email, password },
  });
  return LoginResponse.parse(raw);
}

/* ────────────────────────  USR-006 — 2FA endpoints  ──────────────────────── */

export const TwoFactorEnrollResponse = z.object({
  provisioning_uri: z.string(),
  recovery_codes: z.array(z.string()).length(10),
});
export type TwoFactorEnrollResponse = z.infer<typeof TwoFactorEnrollResponse>;

export async function enrollTwoFactor(accessToken: string): Promise<TwoFactorEnrollResponse> {
  const raw = await apiRequest<unknown>('/api/v1/auth/2fa/enroll', {
    method: 'POST',
    accessToken,
  });
  return TwoFactorEnrollResponse.parse(raw);
}

export async function verifyTwoFactorEnroll(accessToken: string, code: string): Promise<void> {
  await apiRequest('/api/v1/auth/2fa/verify-enroll', {
    method: 'POST',
    accessToken,
    body: { code },
  });
}

export async function disableTwoFactor(accessToken: string, code: string): Promise<void> {
  await apiRequest('/api/v1/auth/2fa/disable', {
    method: 'POST',
    accessToken,
    body: { code },
  });
}

export async function verifyTwoFactorLogin(
  mfaToken: string,
  code: string,
): Promise<LoginSession> {
  const raw = await apiRequest<unknown>('/api/v1/auth/2fa/verify', {
    method: 'POST',
    body: { mfa_token: mfaToken, code },
  });
  return LoginSession.parse(raw);
}

/* ────────────────────────  GDP-080 retention  ──────────────────────── */

export const SystemSetting = z.object({
  setting_key: z.string(),
  setting_value: z.string(),
  data_type: z.string(),
  description: z.string().nullable(),
  updated_at: z.string(),
});
export type SystemSetting = z.infer<typeof SystemSetting>;

export const SystemSettingsList = z.object({
  data: z.array(SystemSetting),
});
export type SystemSettingsList = z.infer<typeof SystemSettingsList>;

export async function listSystemSettings(accessToken: string): Promise<SystemSettingsList> {
  const raw = await apiRequest<unknown>('/api/v1/admin/settings', { accessToken });
  return SystemSettingsList.parse(raw);
}

export async function updateSystemSetting(
  accessToken: string,
  key: string,
  value: string,
): Promise<SystemSetting> {
  const raw = await apiRequest<unknown>(`/api/v1/admin/settings/${encodeURIComponent(key)}`, {
    method: 'PATCH',
    accessToken,
    body: { setting_value: value },
  });
  return SystemSetting.parse(raw);
}

export const RetentionPreview = z.object({
  due: z.object({
    activeClient: z.number().int(),
    archivedClient: z.number().int(),
    auditLog: z.number().int(),
  }),
  upcoming: z.object({
    activeClient: z.number().int(),
    archivedClient: z.number().int(),
    auditLog: z.number().int(),
  }),
  cutoffs: z.object({
    activeClient: z.string(),
    archivedClient: z.string(),
    auditLog: z.string(),
  }),
});
export type RetentionPreview = z.infer<typeof RetentionPreview>;

export async function fetchRetentionPreview(accessToken: string): Promise<RetentionPreview> {
  const raw = await apiRequest<unknown>('/api/v1/admin/retention/preview', { accessToken });
  return RetentionPreview.parse(raw);
}

/**
 * EXP-090 (R2.7) — fetches the rendered Progress Report PDF as a Blob.
 * Bypasses `apiRequest` since the response body is binary, not JSON.
 * Throws `ApiError` with `BINARY_FETCH_FAILED` for non-200 responses
 * since the server-side route returns a JSON envelope on error.
 */
export async function fetchProgressReportPdf(
  accessToken: string,
  clientId: string,
): Promise<Blob> {
  const url = `${apiBaseUrl}/api/v1/clients/${clientId}/progress-report.pdf`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    });
  } catch (cause) {
    throw new ApiError(0, {
      error: 'NETWORK_ERROR',
      message: cause instanceof Error ? cause.message : 'Network request failed',
    });
  }
  if (!response.ok) {
    // Attempt JSON envelope; fall back to a synthetic message.
    let envelope: unknown;
    try {
      envelope = await response.json();
    } catch {
      throw new ApiError(response.status, {
        error: 'BINARY_FETCH_FAILED',
        message: `Server returned ${response.status} for progress-report.pdf`,
      });
    }
    const parsed = ApiErrorEnvelope.safeParse(envelope);
    if (parsed.success) {
      throw new ApiError(response.status, parsed.data);
    }
    throw new ApiError(response.status, {
      error: 'BINARY_FETCH_FAILED',
      message: `Server returned ${response.status} for progress-report.pdf`,
    });
  }
  return await response.blob();
}

export const UserProfile = z.object({
  id: z.string(),
  email: z.string(),
  role: z.enum(['head_coach', 'coach', 'client']),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  language: z.enum(['de', 'en']).optional(),
  /** R1.8 sub-step 3 — for client-role users, their clients.id row. */
  clientId: z.string().nullable().optional(),
  /** R2.4 sub-step 3 — for coach + head_coach users, their coaches.id row. */
  coachId: z.string().nullable().optional(),
});
export type UserProfile = z.infer<typeof UserProfile>;

export async function fetchProfile(accessToken: string): Promise<UserProfile> {
  const raw = await apiRequest<unknown>('/api/v1/users/me', { accessToken });
  // The API emits snake_case for /users/me (first_name, last_name, coach_id,
  // client_id) but the UserProfile schema is camelCase. Translate before
  // parsing so coach_id / client_id surface to consumers like the R2.4
  // Coach Quick View + the R1.8 settings/erasure flows.
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    return UserProfile.parse({
      id: r.id,
      email: r.email,
      role: r.role,
      firstName: r.first_name ?? r.firstName ?? null,
      lastName: r.last_name ?? r.lastName ?? null,
      language: r.language,
      clientId: r.client_id ?? r.clientId ?? null,
      coachId: r.coach_id ?? r.coachId ?? null,
    });
  }
  return UserProfile.parse(raw);
}

/**
 * Persist the signed-in user's preferred language to the server
 * (`users.language`). The Language switcher changes the UI immediately via
 * i18n, but without this the server keeps the default and sends reminder /
 * transactional emails in the wrong language. PATCH /users/me already accepts
 * `language` (USR-009).
 */
export async function updateMyLanguage(
  accessToken: string,
  language: 'de' | 'en',
): Promise<void> {
  await apiRequest('/api/v1/users/me', {
    method: 'PATCH',
    accessToken,
    body: { language },
  });
}

export async function logout(accessToken: string): Promise<void> {
  await apiRequest('/api/v1/auth/logout', { method: 'POST', accessToken });
}

/* ────────────────────────  invite + registration (CRM-012, ONB-020)  ──────────────────────── */

export const InviteInfo = z.object({
  email: z.string(),
  first_name: z.string(),
  language: z.enum(['de', 'en']),
  agreement_version: z.string(),
});
export type InviteInfo = z.infer<typeof InviteInfo>;

export async function getInvite(token: string): Promise<InviteInfo> {
  const raw = await apiRequest<unknown>(`/api/v1/auth/invite/${encodeURIComponent(token)}`);
  return InviteInfo.parse(raw);
}

export interface RegisterFromInviteBody {
  token: string;
  password: string;
  signedName: string;
  agreementVersion: string;
  agreementText: string;
  /** #8 — language the client chose on the activation page; persisted to
   *  the account so emails + tools follow it. Optional for back-compat. */
  language?: 'de' | 'en';
  /** GDP-076 — optional consent grants captured at registration. */
  consents?: { consentType: 'data_processing' | 'email_notifications' | 'ai_insights' | 'analytics'; granted: boolean }[];
}

export const RegisterFromInviteResponse = z.object({
  status: z.literal('ok'),
  user_id: z.string(),
  client_id: z.string(),
});
export type RegisterFromInviteResponse = z.infer<typeof RegisterFromInviteResponse>;

export async function registerFromInvite(
  body: RegisterFromInviteBody,
): Promise<RegisterFromInviteResponse> {
  const raw = await apiRequest<unknown>('/api/v1/auth/register-from-invite', {
    method: 'POST',
    body: {
      token: body.token,
      password: body.password,
      signed_name: body.signedName,
      agreement_version: body.agreementVersion,
      agreement_text: body.agreementText,
      ...(body.language ? { language: body.language } : {}),
      ...(body.consents && body.consents.length > 0
        ? {
            consents: body.consents.map((c) => ({
              consent_type: c.consentType,
              granted: c.granted,
            })),
          }
        : {}),
    },
  });
  return RegisterFromInviteResponse.parse(raw);
}

/* ────────────────────────  password reset (USR-007)  ──────────────────────── */

/**
 * Ask for a reset e-mail. The API answers 200 whether or not the address has
 * an account, so the caller must not branch on the outcome (no enumeration).
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiRequest<unknown>('/api/v1/auth/password-reset/request', {
    method: 'POST',
    body: { email },
  });
}

/**
 * Set a new password with the token from the reset e-mail. Throws `ApiError`
 * 400 (complexity) or 401 (token invalid, expired or already used).
 */
export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  await apiRequest<unknown>('/api/v1/auth/password-reset/confirm', {
    method: 'POST',
    body: { token, newPassword },
  });
}

/* ────────────────────────  clients (CRM-013) — coach surface  ──────────────────────── */

export const ClientStatus = z.enum(['invited', 'active', 'paused', 'graduated', 'archived']);
export type ClientStatus = z.infer<typeof ClientStatus>;

export const ClientListItem = z.object({
  id: z.string(),
  user_id: z.string(),
  coach_id: z.string(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  status: ClientStatus,
  current_step: z.number().int(),
  current_phase: z.number().int(),
  program_start_date: z.string().nullable(),
  expected_graduation: z.string().nullable(),
  phone: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
});
export type ClientListItem = z.infer<typeof ClientListItem>;

export const ClientList = z.object({
  data: z.array(ClientListItem),
  page: z.number().int(),
  per_page: z.number().int(),
  total: z.number().int(),
});
export type ClientList = z.infer<typeof ClientList>;

export interface ListClientsOptions {
  page?: number;
  perPage?: number;
  status?: ClientStatus;
  search?: string;
}

/** Subset of `GET /clients/:id` the client-facing surfaces care about.
 * The full ClientSchema is broader (status / steps / coach_id …) but
 * settings + dashboard only need identity + the two erasure stamps. */
export const ClientSelf = z.object({
  id: z.string(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  current_step: z.number().int(),
  current_phase: z.number().int(),
  /** F-022: payment + coaching start dates. Optional in the response
   *  for back-compat with pre-F-022 prod versions. */
  payment_date: z.string().nullable().optional(),
  coaching_start_date: z.string().nullable().optional(),
  program_start_date: z.string().nullable().optional(),
  /** NL-047 — coach-set graduation date (YYYY-MM-DD). Drives the
   *  post-graduation Dashboard banner's 7-day visibility window. */
  actual_graduation: z.string().nullable().optional(),
  erasure_requested_at: z.string().nullable(),
  erasure_scheduled_for: z.string().nullable(),
});
export type ClientSelf = z.infer<typeof ClientSelf>;

export async function getClientSelf(
  accessToken: string,
  clientId: string,
): Promise<ClientSelf> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}`,
    { accessToken },
  );
  return ClientSelf.parse(raw);
}

/**
 * Set the client's program start date (`YYYY-MM-DD`). Used by the Commitment
 * Agreement's "set to today" button. The client may only set it while it's
 * still unset (server enforces set-once for clients; coaches set it via
 * PATCH /clients/:id or the macOS Overview). Returns the saved date.
 */
export async function setProgramStartDate(
  accessToken: string,
  clientId: string,
  date: string,
): Promise<string> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/program-start-date`,
    { method: 'PUT', accessToken, body: { date } },
  );
  return z.object({ program_start_date: z.string() }).parse(raw).program_start_date;
}

export async function listClients(
  accessToken: string,
  options: ListClientsOptions = {},
): Promise<ClientList> {
  const params = new URLSearchParams();
  if (options.page !== undefined) params.set('page', String(options.page));
  if (options.perPage !== undefined) params.set('per_page', String(options.perPage));
  if (options.status !== undefined) params.set('status', options.status);
  if (options.search !== undefined && options.search.length > 0) {
    params.set('search', options.search);
  }
  const query = params.toString();
  const path = query ? `/api/v1/clients?${query}` : '/api/v1/clients';
  const raw = await apiRequest<unknown>(path, { accessToken });
  return ClientList.parse(raw);
}

/* ────────────────────────  tools (R1.3 — WFL-105 + WFL-106)  ──────────────────────── */

export const ToolCompletionStatus = z.enum([
  'not_started',
  'in_progress',
  'completed',
  'skipped',
]);
export type ToolCompletionStatus = z.infer<typeof ToolCompletionStatus>;

export const ToolCompletion = z.object({
  id: z.string(),
  client_id: z.string(),
  tool_type: z.string(),
  step_number: z.number().int(),
  status: ToolCompletionStatus,
  /**
   * R3.6.x — set when the coach explicitly assigned this tool to the
   * client. Null for legacy direct-submit completions.
   */
  assigned_at: z.string().nullable().optional(),
  completed_at: z.string().nullable(),
  completed_by: z.string().nullable(),
  notes: z.string().nullable(),
  payload: z.unknown().nullable(),
});
export type ToolCompletion = z.infer<typeof ToolCompletion>;

export const ToolCompletionsList = z.object({
  data: z.array(ToolCompletion),
});
export type ToolCompletionsList = z.infer<typeof ToolCompletionsList>;

export interface SubmitToolCompletionBody {
  payload: unknown;
  notes?: string;
}

export async function submitToolCompletion(
  accessToken: string,
  clientId: string,
  toolKey: string,
  body: SubmitToolCompletionBody,
): Promise<ToolCompletion> {
  // NL-027 — when a coach-edit session is active for THIS exact
  // (clientId, toolKey) — armed from the `?coachEdit` token by
  // useToolRouteEffects — route the save to the coach-edit endpoint so it
  // lands on the finalized row + emits the `tool_finalized_edit` audit
  // event. The scoping prevents a save for a different client/tool (e.g. a
  // deferred submit racing a navigation) from picking up the token. The 30
  // bespoke tool forms keep calling this function unchanged.
  const coachEditToken = getActiveCoachEditToken(clientId, toolKey);
  if (coachEditToken) {
    const raw = await apiRequest<unknown>(
      `/api/v1/clients/${encodeURIComponent(clientId)}/tools/${encodeURIComponent(toolKey)}/completions/coach-edit`,
      { method: 'PUT', accessToken, body: { coachEditToken, payload: body.payload } },
    );
    return ToolCompletion.parse(raw);
  }
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/tools/${encodeURIComponent(toolKey)}/completions`,
    { method: 'POST', accessToken, body },
  );
  return ToolCompletion.parse(raw);
}

export async function getToolCompletion(
  accessToken: string,
  clientId: string,
  toolKey: string,
): Promise<ToolCompletion | null> {
  try {
    const raw = await apiRequest<unknown>(
      `/api/v1/clients/${encodeURIComponent(clientId)}/tools/${encodeURIComponent(toolKey)}/completions`,
      { accessToken },
    );
    return ToolCompletion.parse(raw);
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) {
      return null;
    }
    throw cause;
  }
}

/* ─────────────────────────────  Carpe Diem  ───────────────────────────── */

const CarpeDiemAxis = z.enum(['energy', 'work', 'love']);

export const CarpeDiemProtocol = z.object({
  id: z.string(),
  version: z.number().int(),
  isActive: z.boolean(),
  identity: z.object({
    energy: z.string().nullable(),
    work: z.string().nullable(),
    love: z.string().nullable(),
  }),
  matrix: z.object({
    energy: z.array(z.string()),
    work: z.array(z.string()),
    love: z.array(z.string()),
  }),
  builtAt: z.string(),
  builtByUserId: z.string().nullable(),
});
export type CarpeDiemProtocol = z.infer<typeof CarpeDiemProtocol>;

export const CarpeDiemCatalogVirtue = z.object({
  id: z.string(),
  name: z.string(),
  domain: CarpeDiemAxis,
  priority: z.number().int(),
  source: z.string(),
  status: z.enum(['active', 'archived', 'merged']),
  affirmation: z.string().nullable(),
  affirmationAuthor: z.enum(['client', 'coach']).nullable(),
  trackingItemIds: z.array(z.string()),
  createdAt: z.string(),
});
export type CarpeDiemCatalogVirtue = z.infer<typeof CarpeDiemCatalogVirtue>;

const CarpeDiemCatalogResponse = z.object({
  virtues: z.array(CarpeDiemCatalogVirtue),
});

export async function getCarpeDiemProtocol(
  accessToken: string,
  clientId: string,
): Promise<CarpeDiemProtocol | null> {
  try {
    const raw = await apiRequest<unknown>(
      `/api/v1/clients/${encodeURIComponent(clientId)}/carpe-diem/protocol`,
      { accessToken },
    );
    // 204 returns undefined per apiRequest's internal contract.
    if (raw === null || raw === undefined) return null;
    return CarpeDiemProtocol.parse(raw);
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) {
      return null;
    }
    throw cause;
  }
}

export async function getCarpeDiemCatalog(
  accessToken: string,
  clientId: string,
): Promise<CarpeDiemCatalogVirtue[]> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/carpe-diem/virtues`,
    { accessToken },
  );
  return CarpeDiemCatalogResponse.parse(raw).virtues;
}

export async function setCarpeDiemAffirmation(
  accessToken: string,
  clientId: string,
  virtueId: string,
  text: string,
): Promise<void> {
  await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/carpe-diem/virtues/${encodeURIComponent(virtueId)}/affirmation`,
    {
      method: 'PUT',
      accessToken,
      body: { text },
    },
  );
}

/**
 * NL-032 — fetch the client's active virtues + their currently-active
 * affirmation, partitioned by whether the virtue sits in the active
 * Carpe Diem matrix (`inMatrix`) or not (`inPool`).
 */
const AffirmationGroupEntry = z.object({
  virtueId: z.string(),
  virtueName: z.string(),
  domain: z.enum(['energy', 'work', 'love']),
  affirmationText: z.string().nullable(),
  affirmationCreatedAt: z.string().nullable(),
});

export type AffirmationGroupEntry = z.infer<typeof AffirmationGroupEntry>;

const AffirmationsByGroupResponse = z.object({
  inMatrix: z.array(AffirmationGroupEntry),
  inPool: z.array(AffirmationGroupEntry),
});

export type AffirmationsByGroup = z.infer<typeof AffirmationsByGroupResponse>;

export async function getAffirmationsByGroup(
  accessToken: string,
  clientId: string,
): Promise<AffirmationsByGroup> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/affirmations`,
    { accessToken },
  );
  return AffirmationsByGroupResponse.parse(raw);
}

/**
 * NL-023 — Step Popup Framework. Loose response shape; the framework
 * ships with `placeholders: true` for every step until NL1.4-NL1.6 land
 * per-step content modules.
 */
const StepStateZ = z.enum(['not_started', 'current', 'complete']);
const StepPopupContentZ = z
  .object({
    step: z.number().int().min(0).max(10),
    state: StepStateZ,
    placeholders: z.boolean(),
  })
  .passthrough();

export type StepPopupContent = z.infer<typeof StepPopupContentZ> & {
  // Per-step extension fields (NL1.4-NL1.6 fill in)
  baselineKpi?: { energy: number | null; work: number | null; love: number | null } | null;
  topObstacles?: string[];
  commitmentSigned?: boolean;
  targetKpi?: { energy: number | null; work: number | null; love: number | null } | null;
  plusOne?: string[];
  minusOne?: string[];
  identityCount?: number;
  virtueCount?: number;
  behaviorCount?: number;
  confidenceLogCount?: number;
  daysSinceStepStart?: number;
  gratitudeAdded?: boolean;
  weeks?: { weekStart: string; virtueName: string | null }[];
};

export async function getJourneyPopup(
  accessToken: string,
  clientId: string,
  step: number,
): Promise<StepPopupContent> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/journey/popup/${encodeURIComponent(String(step))}`,
    { accessToken },
  );
  return StepPopupContentZ.parse(raw) as StepPopupContent;
}

/**
 * NL-011 — Confidence Log entry surface. The free-form
 * "Today I showed up as my heroic self when I..." text per (client, date).
 */
const ConfidenceLogEntryZ = z.object({
  id: z.string(),
  date: z.string(),
  entryText: z.string(),
  createdAt: z.string(),
});

export type ConfidenceLogEntry = z.infer<typeof ConfidenceLogEntryZ>;

const ConfidenceLogListResponseZ = z.object({
  entries: z.array(ConfidenceLogEntryZ),
});

export async function getConfidenceLogEntries(
  accessToken: string,
  clientId: string,
  opts: { from?: string; to?: string } = {},
): Promise<ConfidenceLogEntry[]> {
  const params = new URLSearchParams();
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  const query = params.toString() ? `?${params.toString()}` : '';
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/confidence-log${query}`,
    { accessToken },
  );
  return ConfidenceLogListResponseZ.parse(raw).entries;
}

export async function upsertConfidenceLogEntry(
  accessToken: string,
  clientId: string,
  date: string,
  entryText: string,
): Promise<ConfidenceLogEntry | null> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/confidence-log/${encodeURIComponent(date)}`,
    { method: 'PUT', accessToken, body: { entryText } },
  );
  if (raw === null || raw === undefined) return null;
  return ConfidenceLogEntryZ.parse(raw);
}

/**
 * NL-019 / NL-022 — Weekly Focus Virtue surface. One row per ISO week.
 */
const WeeklyFocusEntryZ = z.object({
  weekStart: z.string(),
  virtueId: z.string().nullable(),
  virtueName: z.string().nullable(),
  affirmationText: z.string().nullable(),
  reason: z.string().nullable(),
});

export type WeeklyFocusEntry = z.infer<typeof WeeklyFocusEntryZ>;

const WeeklyFocusResponseZ = z.object({
  current: WeeklyFocusEntryZ.nullable(),
  history: z.array(WeeklyFocusEntryZ),
});

export type WeeklyFocusResponse = z.infer<typeof WeeklyFocusResponseZ>;

export async function getWeeklyFocus(
  accessToken: string,
  clientId: string,
  opts: { since?: string } = {},
): Promise<WeeklyFocusResponse> {
  const params = new URLSearchParams();
  if (opts.since) params.set('since', opts.since);
  const query = params.toString() ? `?${params.toString()}` : '';
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/weekly-focus${query}`,
    { accessToken },
  );
  return WeeklyFocusResponseZ.parse(raw);
}

export async function setWeeklyFocus(
  accessToken: string,
  clientId: string,
  weekStart: string,
  virtueId: string | null,
  reason: string,
): Promise<WeeklyFocusEntry> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/weekly-focus/${encodeURIComponent(weekStart)}`,
    { method: 'PUT', accessToken, body: { virtueId, reason } },
  );
  return WeeklyFocusEntryZ.parse(raw);
}

/**
 * NL-041 — Weekly Review v2 (Step 8). One row per ISO week on
 * `weekly_reviews` with three new TEXT columns.
 */
const WeeklyReviewV2EntryZ = z.object({
  weekStart: z.string(),
  reflectionGood: z.string().nullable(),
  reflectionNotGood: z.string().nullable(),
  reflectionNextFocus: z.string().nullable(),
  hasAnyContent: z.boolean(),
});

export type WeeklyReviewV2Entry = z.infer<typeof WeeklyReviewV2EntryZ>;

const WeeklyReviewV2GetResponseZ = z.object({
  current: WeeklyReviewV2EntryZ.nullable(),
});
const WeeklyReviewV2LatestResponseZ = z.object({
  latest: WeeklyReviewV2EntryZ.nullable(),
});

export async function getWeeklyReviewV2(
  accessToken: string,
  clientId: string,
  opts: { weekStart?: string } = {},
): Promise<WeeklyReviewV2Entry | null> {
  const params = new URLSearchParams();
  if (opts.weekStart) params.set('weekStart', opts.weekStart);
  const query = params.toString() ? `?${params.toString()}` : '';
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/weekly-review-v2${query}`,
    { accessToken },
  );
  return WeeklyReviewV2GetResponseZ.parse(raw).current;
}

export async function getLatestWeeklyReviewV2(
  accessToken: string,
  clientId: string,
): Promise<WeeklyReviewV2Entry | null> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/weekly-review-v2/latest`,
    { accessToken },
  );
  return WeeklyReviewV2LatestResponseZ.parse(raw).latest;
}

export async function setWeeklyReviewV2(
  accessToken: string,
  clientId: string,
  weekStart: string,
  body: {
    reflectionGood: string | null;
    reflectionNotGood: string | null;
    reflectionNextFocus: string | null;
  },
): Promise<WeeklyReviewV2Entry> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/weekly-review-v2/${encodeURIComponent(weekStart)}`,
    { method: 'PUT', accessToken, body },
  );
  return WeeklyReviewV2EntryZ.parse(raw);
}

export async function listToolCompletions(
  accessToken: string,
  clientId: string,
): Promise<ToolCompletionsList> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/tools`,
    { accessToken },
  );
  return ToolCompletionsList.parse(raw);
}

/**
 * R3.6.x — coach assigns a tool to a client. Client surfaces shown via
 * `listToolCompletions(accessToken, 'me')`.
 */
export async function assignTool(
  accessToken: string,
  clientId: string,
  toolKey: string,
  body: { notes?: string } = {},
): Promise<ToolCompletion> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/tools/${encodeURIComponent(toolKey)}/assign`,
    { method: 'POST', accessToken, body },
  );
  return ToolCompletion.parse(raw);
}

export async function unassignTool(
  accessToken: string,
  clientId: string,
  toolKey: string,
): Promise<void> {
  await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/tools/${encodeURIComponent(toolKey)}/assign`,
    { method: 'DELETE', accessToken },
  );
}

/* ────────────────────────  daily tracking (R1.4 — TRK-048..TRK-052)  ──────────────────────── */

export const DayType = z.enum(['good', 'fallback']);
export type DayType = z.infer<typeof DayType>;

export const DailyTracking = z.object({
  id: z.string(),
  client_id: z.string(),
  date: z.string(),
  energy_kpi: z.number().int().nullable(),
  work_kpi: z.number().int().nullable(),
  love_kpi: z.number().int().nullable(),
  am_bookend: z.boolean().nullable(),
  cbr_deep_work: z.boolean().nullable(),
  pm_bookend: z.boolean().nullable(),
  one_thing_done: z.boolean().nullable(),
  confidence_log: z.boolean().nullable(),
  virtue_affirmations: z.boolean().nullable(),
  masterpiece_day: z.boolean().nullable(),
  fulfillment_score: z.number().int().nullable(),
  one_thing_declaration: z.string().nullable(),
  /** NL-033 — client's "My #1 thing to get up today" free-text. Null = not yet entered. */
  daily_win_text: z.string().nullable(),
  day_type: DayType.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DailyTracking = z.infer<typeof DailyTracking>;

export const DailyTrackingList = z.object({ data: z.array(DailyTracking) });
export type DailyTrackingList = z.infer<typeof DailyTrackingList>;

export interface DailyTrackingUpsertBody {
  date: string;
  energy_kpi?: number | null;
  work_kpi?: number | null;
  love_kpi?: number | null;
  am_bookend?: boolean | null;
  cbr_deep_work?: boolean | null;
  pm_bookend?: boolean | null;
  one_thing_done?: boolean | null;
  confidence_log?: boolean | null;
  virtue_affirmations?: boolean | null;
  masterpiece_day?: boolean | null;
  one_thing_declaration?: string | null;
  /** NL-033 — Daily WIN free-text. Empty string = clear; null = no-op. */
  daily_win_text?: string | null;
  day_type?: DayType | null;
}

export async function submitDailyTracking(
  accessToken: string,
  clientId: string,
  body: DailyTrackingUpsertBody,
): Promise<DailyTracking> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/daily-tracking`,
    { method: 'POST', accessToken, body },
  );
  return DailyTracking.parse(raw);
}

export interface ListDailyTrackingOptions {
  date?: string;
  from?: string;
  to?: string;
}

export async function listDailyTracking(
  accessToken: string,
  clientId: string,
  options: ListDailyTrackingOptions = {},
): Promise<DailyTrackingList> {
  const params = new URLSearchParams();
  if (options.date) params.set('date', options.date);
  if (options.from) params.set('from', options.from);
  if (options.to) params.set('to', options.to);
  const query = params.toString();
  const path = query
    ? `/api/v1/clients/${encodeURIComponent(clientId)}/daily-tracking?${query}`
    : `/api/v1/clients/${encodeURIComponent(clientId)}/daily-tracking`;
  const raw = await apiRequest<unknown>(path, { accessToken });
  return DailyTrackingList.parse(raw);
}

export const TrackingStreak = z.object({
  currentStreak: z.number().int(),
  longestStreak: z.number().int(),
  totalDays: z.number().int(),
});
export type TrackingStreak = z.infer<typeof TrackingStreak>;

export async function getTrackingStreak(
  accessToken: string,
  clientId: string,
): Promise<TrackingStreak> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/daily-tracking/streak`,
    { accessToken },
  );
  return TrackingStreak.parse(raw);
}

export const CorrelationPoint = z.object({
  r: z.number().nullable(),
  n: z.number().int(),
});
export type CorrelationPoint = z.infer<typeof CorrelationPoint>;
export const ClientCorrelation = z.object({
  rows: z.number().int(),
  windowDays: z.number().int(),
  energy: CorrelationPoint,
  work: CorrelationPoint,
  love: CorrelationPoint,
});
export type ClientCorrelation = z.infer<typeof ClientCorrelation>;

export async function getClientCorrelation(
  accessToken: string,
  clientId: string,
  days = 30,
): Promise<ClientCorrelation> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/daily-tracking/correlation?days=${encodeURIComponent(String(days))}`,
    { accessToken },
  );
  return ClientCorrelation.parse(raw);
}

/* DSH-059 — client-facing dashboard (R1.8 sub-step 3). */
const RitualKey = z.enum([
  'am_bookend',
  'cbr_deep_work',
  'pm_bookend',
  'one_thing_done',
  'confidence_log',
  'virtue_affirmations',
  'masterpiece_day',
]);
export const ClientDashboard = z.object({
  todayChecklist: z.array(z.object({ key: RitualKey, done: z.boolean() })),
  fulfillmentToday: z.number().int().min(0).max(100).nullable(),
  // Optional for resilience to API/web version skew (a pre-feat4 API or the
  // dev demo backend omits it) — the card falls back to `fulfillmentToday`.
  fulfillment7dAvg: z.number().int().min(0).max(100).nullable().optional(),
  fulfillmentTrend7d: z.array(
    z.object({ date: z.string(), score: z.number().int().nullable() }),
  ),
  kpiTrend30d: z.array(
    z.object({
      date: z.string(),
      energy: z.number().int().nullable(),
      work: z.number().int().nullable(),
      love: z.number().int().nullable(),
    }),
  ),
  streak: z.object({
    currentStreak: z.number().int(),
    longestStreak: z.number().int(),
    totalDays: z.number().int(),
  }),
  weeklyFocus: z.string().nullable(),
  nextSessionAt: z.string().nullable(),
  // F-013 (2026-05-22): zoom link + step for the next scheduled session.
  // Optional with default null so older API responses don't fail schema
  // validation on a freshly-deployed web bundle.
  nextSessionVideoLink: z.string().nullable().optional().default(null),
  nextSessionStep: z.number().int().min(0).max(9).nullable().optional().default(null),
  upcomingMilestones: z.array(
    z.object({
      kind: z.enum(['sealed_letter_unseal', 'ninety_day_checkin']),
      date: z.string(),
      daysUntil: z.number().int(),
    }),
  ),
  /** AI-067 — 0..2 rule-based positive-framing insights. */
  smartInsights: z
    .array(
      z.object({
        kind: z.enum(['bookend_correlation', 'streak_motivation', 'weekly_summary']),
        title: z.string(),
        body: z.string(),
      }),
    )
    .default([]),
  /** STP-044 — unacknowledged celebration events from the last 14 days (max 5). */
  pendingCelebrations: z
    .array(
      z.object({
        id: z.string(),
        trigger_type: z.string(),
        triggered_at: z.string(),
      }),
    )
    .default([]),
  /** MSG-146 — unread coach-→-client messages addressed to this client. */
  unreadMessageCount: z.number().int().min(0).default(0),
  /** #16 — Step-1 Compass target KPIs; null until the coach sets them.
   *  Optional/nullable for back-compat with pre-#16 prod responses. */
  targetKpis: z
    .object({
      energy: z.number().int().min(1).max(10),
      work: z.number().int().min(1).max(10),
      love: z.number().int().min(1).max(10),
    })
    .nullable()
    .optional(),
});
export type ClientDashboard = z.infer<typeof ClientDashboard>;
export type SmartInsight = ClientDashboard['smartInsights'][number];
export type PendingCelebration = ClientDashboard['pendingCelebrations'][number];

export async function getClientDashboard(
  accessToken: string,
  clientId: string,
): Promise<ClientDashboard> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/dashboard`,
    { accessToken },
  );
  return ClientDashboard.parse(raw);
}

export async function ackCelebration(
  accessToken: string,
  clientId: string,
  celebrationId: string,
): Promise<{ id: string; acknowledged: true }> {
  return apiRequest<{ id: string; acknowledged: true }>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/celebrations/${encodeURIComponent(celebrationId)}/ack`,
    { method: 'PATCH', accessToken },
  );
}

/* DSH-062 — program-progress timeline (R1.8 sub-step 5). */
const TimelineStatus = z.enum(['completed', 'current', 'future']);
const PhaseGateOutcome = z.enum(['pass', 'conditional', 'fail']);
const TimelineStepEntry = z.object({
  kind: z.literal('step'),
  stepNumber: z.number().int().min(0).max(9),
  status: TimelineStatus,
  isPhaseGate: z.boolean(),
  phaseGateOutcome: PhaseGateOutcome.optional(),
  hasBufferWeek: z.boolean(),
  bufferWeekStartedOn: z.string().optional(),
});
const TimelineGraduationEntry = z.object({
  kind: z.literal('graduation'),
  status: TimelineStatus,
  actualOn: z.string().optional(),
  projectedOn: z.string().optional(),
});
const TimelineMilestoneEntry = z.object({
  kind: z.literal('milestone'),
  milestone: z.literal('ninety_day_checkin'),
  date: z.string(),
  daysUntil: z.number().int(),
  status: TimelineStatus,
});
export const ProgramTimeline = z.object({
  today: z.string(),
  entries: z.array(
    z.union([TimelineStepEntry, TimelineGraduationEntry, TimelineMilestoneEntry]),
  ),
  projectedGraduation: z.string().nullable(),
});
export type ProgramTimeline = z.infer<typeof ProgramTimeline>;
export type TimelineEntry = ProgramTimeline['entries'][number];

export async function getProgramTimeline(
  accessToken: string,
  clientId: string,
): Promise<ProgramTimeline> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/timeline`,
    { accessToken },
  );
  return ProgramTimeline.parse(raw);
}

/* GDP-076 — GDPR Consent Management (R1.9 sub-step 1). */
export const ConsentTypeEnum = z.enum([
  'data_processing',
  'email_notifications',
  'ai_insights',
  'analytics',
]);
export type ConsentType = z.infer<typeof ConsentTypeEnum>;

export const ConsentState = z.object({
  consent_type: ConsentTypeEnum,
  granted: z.boolean(),
  version: z.string(),
  granted_at: z.string(),
  is_current_version: z.boolean(),
});
export type ConsentState = z.infer<typeof ConsentState>;

export const ConsentSnapshot = z.object({
  active_legal_version: z.string(),
  privacy_policy_version: z.string(),
  dpa_version: z.string(),
  consents: z.array(ConsentState),
});
export type ConsentSnapshot = z.infer<typeof ConsentSnapshot>;

export async function getConsent(accessToken: string): Promise<ConsentSnapshot> {
  const raw = await apiRequest<unknown>('/api/v1/consent', { accessToken });
  return ConsentSnapshot.parse(raw);
}

export const PostConsentResult = z.object({
  consent_type: ConsentTypeEnum,
  granted: z.boolean(),
  version: z.string(),
  granted_at: z.string(),
});
export type PostConsentResult = z.infer<typeof PostConsentResult>;

/* GDP-077 — Right to Data Portability (R1.9 sub-step 2). Synchronous
 * pilot implementation: POST returns the export inline. */
export const ClientExportEnvelope = z.object({
  export: z.unknown(),
  summary_md: z.string(),
});
export type ClientExportEnvelope = z.infer<typeof ClientExportEnvelope>;

/* GDP-078 — Right to Erasure (R1.9 sub-step 3). */
export const ErasureRequestResult = z.object({
  erasure_requested_at: z.string(),
  erasure_scheduled_for: z.string(),
});
export type ErasureRequestResult = z.infer<typeof ErasureRequestResult>;

export async function requestErasure(
  accessToken: string,
  clientId: string,
): Promise<ErasureRequestResult> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/erasure-request`,
    { method: 'POST', accessToken },
  );
  return ErasureRequestResult.parse(raw);
}

export async function cancelErasure(
  accessToken: string,
  clientId: string,
): Promise<unknown> {
  return apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/erasure-cancel`,
    { method: 'POST', accessToken },
  );
}

export async function requestDataExport(
  accessToken: string,
  clientId: string,
): Promise<ClientExportEnvelope> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/export-request`,
    { method: 'POST', accessToken },
  );
  return ClientExportEnvelope.parse(raw);
}

export async function postConsent(
  accessToken: string,
  consentType: ConsentType,
  granted: boolean,
): Promise<PostConsentResult> {
  const raw = await apiRequest<unknown>('/api/v1/consent', {
    method: 'POST',
    accessToken,
    body: { consent_type: consentType, granted },
  });
  return PostConsentResult.parse(raw);
}

export const NeverMissTwiceStatus = z.object({
  tripped: z.boolean(),
  missedDates: z.array(z.string()),
  lastMissDate: z.string().optional(),
  windowDays: z.number().int(),
});
export type NeverMissTwiceStatus = z.infer<typeof NeverMissTwiceStatus>;

export async function getNeverMissTwice(
  accessToken: string,
  clientId: string,
  days = 30,
): Promise<NeverMissTwiceStatus> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/daily-tracking/never-miss-twice?days=${encodeURIComponent(String(days))}`,
    { accessToken },
  );
  return NeverMissTwiceStatus.parse(raw);
}

export const LegacyLetter = z.object({
  id: z.string(),
  client_id: z.string(),
  letter_type: z.enum(['from_future_self', 'to_future_self']),
  letter_text: z.string(),
  signed_name: z.string().nullable(),
  written_date: z.string(),
  seal_date: z.string().nullable(),
  is_sealed: z.boolean(),
  created_at: z.string(),
});
export type LegacyLetter = z.infer<typeof LegacyLetter>;
export const LegacyLetterList = z.object({ data: z.array(LegacyLetter) });
export type LegacyLetterList = z.infer<typeof LegacyLetterList>;

export interface CreateLegacyLetterBody {
  letter_type: 'from_future_self' | 'to_future_self';
  letter_text: string;
  signed_name?: string | null;
  written_date: string;
  seal_date?: string | null;
  is_sealed: boolean;
}

export async function listLegacyLetters(
  accessToken: string,
  clientId: string,
): Promise<LegacyLetterList> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/legacy-letters`,
    { accessToken },
  );
  return LegacyLetterList.parse(raw);
}

export async function createLegacyLetter(
  accessToken: string,
  clientId: string,
  body: CreateLegacyLetterBody,
): Promise<LegacyLetter> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${encodeURIComponent(clientId)}/legacy-letters`,
    { method: 'POST', accessToken, body },
  );
  return LegacyLetter.parse(raw);
}

/* ────────────────  DSH-060 / DSH-144 coach portfolio (Quick View)  ──────────────── */

export const PortfolioMetric = z.object({
  clientId: z.string(),
  fullName: z.string(),
  status: z.enum(['invited', 'active', 'paused', 'graduated', 'archived']),
  currentStep: z.number().int(),
  currentPhase: z.number().int(),
  fulfillment7dAvg: z.number().nullable(),
  missedTrackingDays: z.number().int(),
  consecutiveFallbackDays: z.number().int(),
  fulfillmentDrop: z.number(),
  attentionScore: z.number(),
  tone: z.enum(['green', 'yellow', 'red']),
});

export const CoachPortfolio = z.object({
  today: z.string(),
  metrics: z.array(PortfolioMetric),
  attentionQueue: z.array(PortfolioMetric),
  activeCount: z.number().int(),
  totalCount: z.number().int(),
});

export type CoachPortfolioData = z.infer<typeof CoachPortfolio>;
export type PortfolioMetricData = z.infer<typeof PortfolioMetric>;

export async function getCoachPortfolio(
  accessToken: string,
  coachId: string,
): Promise<CoachPortfolioData> {
  const raw = await apiRequest<unknown>(
    `/api/v1/coaches/${encodeURIComponent(coachId)}/portfolio`,
    { accessToken },
  );
  return CoachPortfolio.parse(raw);
}

/* ────────────────  CRM-143 client CSV import (head-coach)  ──────────────── */

export const ImportPreviewSummary = z.object({
  records_total: z.number().int(),
  records_valid: z.number().int(),
  records_failed: z.number().int(),
});

export const ImportPreviewSampleRow = z.object({
  rowNumber: z.number().int(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  coachEmail: z.string(),
  coachId: z.string().nullable(),
});

export const ImportError = z.object({
  rowNumber: z.number().int(),
  email: z.string(),
  reason: z.enum([
    'invalid_email',
    'missing_field',
    'duplicate_in_csv',
    'duplicate_in_db',
    'coach_not_found',
    'header_mismatch',
    'header_missing',
  ]),
  message: z.string(),
});

export const ImportPreviewResponse = z.object({
  summary: ImportPreviewSummary,
  sample: z.array(ImportPreviewSampleRow),
  errors: z.array(ImportError),
});

export const ImportCommitResponse = z.object({
  log: z.object({
    id: z.string(),
    records_total: z.number().int(),
    records_success: z.number().int(),
    records_failed: z.number().int(),
    created_at: z.string(),
  }),
  errors: z.array(ImportError),
});

export type ImportPreview = z.infer<typeof ImportPreviewResponse>;
export type ImportCommit = z.infer<typeof ImportCommitResponse>;
export type ImportPreviewError = z.infer<typeof ImportError>;

export async function previewClientImport(
  accessToken: string,
  body: { csv: string; filename: string },
): Promise<ImportPreview> {
  const raw = await apiRequest<unknown>(
    '/api/v1/admin/imports/clients/preview',
    { method: 'POST', accessToken, body },
  );
  return ImportPreviewResponse.parse(raw);
}

export async function commitClientImport(
  accessToken: string,
  body: { csv: string; filename: string },
): Promise<ImportCommit> {
  const raw = await apiRequest<unknown>('/api/v1/admin/imports/clients', {
    method: 'POST',
    accessToken,
    body,
  });
  return ImportCommitResponse.parse(raw);
}

/* ──────────────  MSG-147 — notification preferences (R2.5 s2)  ────────────── */

export const NotificationCategory = z.enum([
  'tracking_reminder',
  'session_reminder',
  'milestone',
  'coach_message',
  'sealed_letter',
  'gate_result',
  'weekly_review',
]);
export type NotificationCategory = z.infer<typeof NotificationCategory>;

export const NotificationChannel = z.enum(['email', 'in_app', 'push']);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

export const NotificationPreferenceEntry = z.object({
  category: NotificationCategory,
  channel: NotificationChannel,
  enabled: z.boolean(),
});
export type NotificationPreferenceEntry = z.infer<typeof NotificationPreferenceEntry>;

export const NotificationPreferences = z.object({
  preferences: z.array(NotificationPreferenceEntry),
});
export type NotificationPreferences = z.infer<typeof NotificationPreferences>;

export async function getNotificationPreferences(
  accessToken: string,
): Promise<NotificationPreferences> {
  const raw = await apiRequest<unknown>('/api/v1/users/me/notification-preferences', {
    method: 'GET',
    accessToken,
  });
  return NotificationPreferences.parse(raw);
}

export async function updateNotificationPreferences(
  accessToken: string,
  body: { preferences: NotificationPreferenceEntry[] },
): Promise<NotificationPreferences> {
  const raw = await apiRequest<unknown>('/api/v1/users/me/notification-preferences', {
    method: 'PUT',
    accessToken,
    body,
  });
  return NotificationPreferences.parse(raw);
}

/* ──────────────  MSG-146 — in-app messaging (R2.5 s5)  ────────────── */

export const ThreadMessage = z.object({
  id: z.string(),
  clientId: z.string(),
  senderUserId: z.string(),
  recipientUserId: z.string(),
  body: z.string(),
  readAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ThreadMessage = z.infer<typeof ThreadMessage>;

export const ThreadResponse = z.object({
  messages: z.array(ThreadMessage),
});
export type ThreadResponse = z.infer<typeof ThreadResponse>;

export async function listMessages(
  accessToken: string,
  clientId: string,
  options: { before?: string; limit?: number } = {},
): Promise<ThreadResponse> {
  const params = new URLSearchParams();
  if (options.before) params.set('before', options.before);
  if (options.limit) params.set('limit', String(options.limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const raw = await apiRequest<unknown>(`/api/v1/clients/${clientId}/messages${qs}`, {
    method: 'GET',
    accessToken,
  });
  return ThreadResponse.parse(raw);
}

export async function sendMessage(
  accessToken: string,
  clientId: string,
  body: string,
): Promise<ThreadMessage> {
  const raw = await apiRequest<unknown>(`/api/v1/clients/${clientId}/messages`, {
    method: 'POST',
    accessToken,
    body: { body },
  });
  return ThreadMessage.parse(raw);
}

export async function markMessageRead(
  accessToken: string,
  messageId: string,
): Promise<void> {
  await apiRequest<unknown>(`/api/v1/messages/${messageId}/read`, {
    method: 'PATCH',
    accessToken,
  });
}

/* ─────────────────────────────  F-015 Hero Tracker  ───────────────────────────── */

export const TrackingItemCategory = z.enum(['am_bookend', 'pm_bookend', 'anywhere']);
export const TrackingItemType = z.enum(['yes_no', 'number']);
export const TrackingItemCreatedBy = z.enum(['client', 'coach', 'tool', 'system']);

export const VirtueAxis = z.enum(['energy', 'work', 'love']);
export type VirtueAxis = z.infer<typeof VirtueAxis>;

export const TrackingItemVirtueBinding = z.object({
  id: z.string(),
  name: z.string(),
  axis: VirtueAxis,
});
export type TrackingItemVirtueBinding = z.infer<typeof TrackingItemVirtueBinding>;

/** NL-037 — per-behavior day-type categorization (subset of good/fallback). */
export const TrackingItemDayType = z.enum(['good', 'fallback']);
export type TrackingItemDayType = z.infer<typeof TrackingItemDayType>;

export const TrackingItem = z.object({
  id: z.string(),
  clientId: z.string(),
  label: z.string(),
  category: TrackingItemCategory,
  itemType: TrackingItemType,
  defaultValue: z.string(),
  sortOrder: z.number().int().min(0),
  createdBy: TrackingItemCreatedBy,
  createdByToolKey: z.string().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** F-025 / #15 — explicit per-item weight (1/2/3). null = legacy derivation
   *  from creator/mandatory. Clients may set 1 or 2; 3 is coach-reserved. */
  weight: z.number().int().nullable().default(null),
  /** F-031 — active virtue bindings (E/W/L). */
  virtues: z.array(TrackingItemVirtueBinding).default([]),
  /** F-034 — direct axis tags. Empty = untagged. */
  axes: z.array(z.enum(['energy', 'work', 'love'])).default([]),
  /** NL-037 — per-behavior day-type categorization. Empty = both. */
  dayTypes: z.array(TrackingItemDayType).default([]),
  /** NL-038 — key-behavior flag. 0..1 per (client, axis). */
  isKeyBehavior: z.boolean().default(false),
});
export type TrackingItem = z.infer<typeof TrackingItem>;

export const TrackingValue = z.object({
  trackingItemId: z.string(),
  date: z.string(),
  value: z.string(),
});
export type TrackingValue = z.infer<typeof TrackingValue>;

const TrackingItemsList = z.object({ data: z.array(TrackingItem) });
const TrackingValuesList = z.object({ data: z.array(TrackingValue) });

export async function listTrackingItems(
  accessToken: string,
  clientId: string,
): Promise<TrackingItem[]> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${clientId}/tracking-items`,
    { method: 'GET', accessToken },
  );
  return TrackingItemsList.parse(raw).data;
}

export async function createTrackingItem(
  accessToken: string,
  clientId: string,
  body: {
    label: string;
    category: z.infer<typeof TrackingItemCategory>;
    itemType: z.infer<typeof TrackingItemType>;
    defaultValue?: string;
  },
): Promise<TrackingItem> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${clientId}/tracking-items`,
    { method: 'POST', accessToken, body },
  );
  return TrackingItem.parse(raw);
}

export async function updateTrackingItem(
  accessToken: string,
  clientId: string,
  itemId: string,
  patch: {
    label?: string;
    category?: z.infer<typeof TrackingItemCategory>;
    itemType?: z.infer<typeof TrackingItemType>;
    defaultValue?: string;
    sortOrder?: number;
    archived?: boolean;
    /** NL-037 — replace the day-type tags. Empty = "applies to both day types". */
    dayTypes?: TrackingItemDayType[];
    /** NL-038 — toggle key-behavior. Server returns 422 on per-axis conflict. */
    isKeyBehavior?: boolean;
    /** F-025 / #15 — explicit weight (1/2/3), or null to revert to legacy
     *  derivation. The client UI only sends 1 or 2 (3 is coach-reserved). */
    weight?: number | null;
  },
): Promise<TrackingItem> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${clientId}/tracking-items/${itemId}`,
    { method: 'PATCH', accessToken, body: patch },
  );
  return TrackingItem.parse(raw);
}

export async function deleteTrackingItem(
  accessToken: string,
  clientId: string,
  itemId: string,
): Promise<void> {
  await apiRequest<unknown>(
    `/api/v1/clients/${clientId}/tracking-items/${itemId}`,
    { method: 'DELETE', accessToken },
  );
}

export async function reorderTrackingItems(
  accessToken: string,
  clientId: string,
  category: z.infer<typeof TrackingItemCategory>,
  orderedItemIds: string[],
): Promise<void> {
  await apiRequest<unknown>(
    `/api/v1/clients/${clientId}/tracking-items/reorder`,
    { method: 'POST', accessToken, body: { category, orderedItemIds } },
  );
}

export async function getTrackingValues(
  accessToken: string,
  clientId: string,
  opts: { date: string } | { from: string; to: string },
): Promise<TrackingValue[]> {
  const qs =
    'date' in opts
      ? `?date=${opts.date}`
      : `?from=${opts.from}&to=${opts.to}`;
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${clientId}/tracking-values${qs}`,
    { method: 'GET', accessToken },
  );
  return TrackingValuesList.parse(raw).data;
}

export async function upsertTrackingValues(
  accessToken: string,
  clientId: string,
  values: TrackingValue[],
): Promise<{ written: number }> {
  const raw = await apiRequest<unknown>(
    `/api/v1/clients/${clientId}/tracking-values`,
    { method: 'POST', accessToken, body: { values } },
  );
  return z.object({ ok: z.literal(true), written: z.number().int() }).parse(raw);
}

/* ─────────────────────────────  F-031 Carpe Diem bindings  ───────────────────────────── */

/**
 * F-031 (2026-05-24) — replace the virtue bindings on a tracker item.
 * Server now permits client-self-write for their own client_id (the
 * client owns their daily practice).
 *
 * (The catalog fetch lives above as `getCarpeDiemCatalog` — same
 * endpoint, predates F-031.)
 */
export async function setTrackingItemVirtues(
  accessToken: string,
  clientId: string,
  trackingItemId: string,
  virtueIds: string[],
): Promise<void> {
  await apiRequest<unknown>(
    `/api/v1/clients/${clientId}/carpe-diem/tracking-items/${trackingItemId}/virtues`,
    { method: 'PUT', accessToken, body: { virtueIds } },
  );
}
